// HEIC / HEIF photos (what an iPhone takes) in browsers that can't read them: Chrome, Edge and Firefox on
// a desktop. Safari reads them itself and never comes here. The decoding runs in lib/heicWorker.ts, off the
// page, with libheif's plain-JavaScript build (no WebAssembly, so the app's Content-Security-Policy stays as
// it is); the worker and its decoder load the first time a HEIC photo needs them and stop again after a
// minute with nothing to do.
//   - shrinkImage turns a HEIC being added into a JPEG, so every browser can show it;
//   - heicPreviewUrl draws a HEIC already saved (added before, or straight in Smartsheet) as a JPEG data:
//     URL (the CSP allows data: images), so its thumbnail and full view show too.

/** the HEIC / HEIF image types. */
export const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'] as const;

/** whether a MIME type is a HEIC / HEIF one (case and parameters ignored). */
export function isHeicType(mime: string | null | undefined): boolean {
  const t = String(mime ?? '').split(';')[0]!.trim().toLowerCase();
  return (HEIC_TYPES as readonly string[]).includes(t);
}

/** whether a file name ends in .heic / .heif. */
export const isHeicName = (name: string | null | undefined): boolean => /\.(heic|heif)$/i.test(String(name ?? '').trim());

// the ftyp brands of an HEVC-coded HEIF file (an AVIF file is a HEIF too, but AV1-coded: browsers read those)
const HEVC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis']);

/** whether a file's first bytes say it is a HEIC: an ISO "ftyp" box naming an HEVC brand, as its main brand
 *  or a compatible one (some phones write "mif1" first). Reads the type from the bytes, whatever the file's
 *  name or MIME type say. */
export function isHeicBytes(head: Uint8Array): boolean {
  if (head.length < 16) return false;
  const text = (at: number) => String.fromCharCode(head[at]!, head[at + 1]!, head[at + 2]!, head[at + 3]!);
  if (text(4) !== 'ftyp') return false;
  const size = ((head[0]! << 24) | (head[1]! << 16) | (head[2]! << 8) | head[3]!) >>> 0;
  const end = Math.min(size || head.length, head.length);
  if (HEVC_BRANDS.has(text(8))) return true;
  for (let at = 16; at + 4 <= end; at += 4) if (HEVC_BRANDS.has(text(at))) return true;
  return false;
}

/** a blob's bytes (FileReader where Blob.arrayBuffer is missing: older engines, the test DOM). */
function bytesOf(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error ?? new Error('Could not read the file'));
    r.readAsArrayBuffer(blob);
  });
}

/** whether a file is a HEIC, from its first bytes. */
export async function sniffHeic(blob: Blob): Promise<boolean> {
  try {
    return isHeicBytes(new Uint8Array(await bytesOf(blob.slice(0, 64))));
  } catch {
    return false;
  }
}

/** a decoded HEIC: its RGBA pixels, row by row. */
export interface HeicPixels { width: number; height: number; data: Uint8ClampedArray<ArrayBuffer> }

/** what the page sends the worker, and what comes back */
export interface HeicRequest { id: number; buffer: ArrayBuffer }
export type HeicReply = { id: number; width: number; height: number; data: ArrayBuffer } | { id: number; error: string };

/** a photo the HEIC reader couldn't read, with a message fit to show. */
export class HeicError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HeicError';
  }
}

const IDLE_MS = 60_000; // the decoder holds on to a lot of memory: let it go once nothing is waiting
const TIMEOUT_MS = 90_000; // a 48 MP photo takes a few seconds; anything past this has gone wrong

let worker: Worker | null = null;
let seq = 0;
let idle: ReturnType<typeof setTimeout> | undefined;
const waiting = new Map<number, { resolve: (p: HeicPixels) => void; reject: (e: Error) => void }>();

function stop() {
  worker?.terminate();
  worker = null;
  for (const w of waiting.values()) w.reject(new HeicError('The HEIC photo couldn’t be read.'));
  waiting.clear();
}

function heicWorker(): Worker {
  if (worker) return worker;
  if (typeof Worker === 'undefined') throw new HeicError('This browser can’t read HEIC photos.');
  const w = new Worker(new URL('./heicWorker.ts', import.meta.url), { type: 'module', name: 'heic' });
  w.onmessage = (e: MessageEvent<HeicReply>) => {
    const r = e.data;
    const p = waiting.get(r.id);
    if (!p) return;
    waiting.delete(r.id);
    if ('error' in r) p.reject(new HeicError(r.error || 'The HEIC photo couldn’t be read.'));
    else p.resolve({ width: r.width, height: r.height, data: new Uint8ClampedArray(r.data) });
  };
  // it couldn't load, or it ran out of memory: everything waiting fails, and the next photo starts afresh
  w.onerror = (e) => {
    e.preventDefault();
    stop();
  };
  worker = w;
  return w;
}

/** decode a HEIC / HEIF file to pixels, in the worker. Rejects with a HeicError when it can't. */
export async function decodeHeic(blob: Blob): Promise<HeicPixels> {
  const buffer = await bytesOf(blob);
  clearTimeout(idle);
  const id = ++seq;
  try {
    return await new Promise<HeicPixels>((resolve, reject) => {
      const timer = setTimeout(() => {
        waiting.delete(id);
        reject(new HeicError('Reading the HEIC photo took too long.'));
      }, TIMEOUT_MS);
      waiting.set(id, {
        resolve: (p) => { clearTimeout(timer); resolve(p); },
        reject: (e) => { clearTimeout(timer); reject(e); },
      });
      try {
        heicWorker().postMessage({ id, buffer } satisfies HeicRequest, [buffer]);
      } catch (e) {
        waiting.delete(id);
        clearTimeout(timer);
        reject(e instanceof HeicError ? e : new HeicError('This browser can’t read HEIC photos.'));
      }
    });
  } finally {
    if (!waiting.size) {
      clearTimeout(idle);
      idle = setTimeout(stop, IDLE_MS);
    }
  }
}

/** decoded pixels on a canvas, at full size (what shrinkImage scales and encodes from). */
export function heicCanvas(px: HeicPixels): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = px.width;
  canvas.height = px.height;
  const g = canvas.getContext('2d');
  if (!g) throw new HeicError('The HEIC photo couldn’t be drawn.');
  g.putImageData(new ImageData(px.data, px.width, px.height), 0, 0);
  return canvas;
}

const previews = new Map<string, Promise<string>>();

/** a saved HEIC photo (its file URL) as a JPEG data: URL, its long edge at most `maxEdge` px. Each photo is
 *  read once per size and remembered for the visit; a failed one may be tried again. */
export function heicPreviewUrl(src: string, maxEdge: number): Promise<string> {
  const key = `${maxEdge}:${src}`;
  const known = previews.get(key);
  if (known) return known;
  const made = (async () => {
    const res = await fetch(src, { credentials: 'same-origin' });
    if (!res.ok) throw new HeicError('The photo couldn’t be loaded.');
    const full = heicCanvas(await decodeHeic(await res.blob()));
    try {
      const k = Math.min(1, maxEdge / Math.max(full.width, full.height));
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(full.width * k));
      out.height = Math.max(1, Math.round(full.height * k));
      const g = out.getContext('2d');
      if (!g) throw new HeicError('The HEIC photo couldn’t be drawn.');
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = 'high';
      g.drawImage(full, 0, 0, out.width, out.height);
      const url = out.toDataURL('image/jpeg', 0.85);
      out.width = out.height = 0;
      return url;
    } finally {
      full.width = full.height = 0; // hand the pixels back straight away
    }
  })();
  previews.set(key, made);
  made.catch(() => previews.delete(key));
  return made;
}
